// CONTRATO da organização: o catálogo de planos (/v1/plans) e a assinatura dela
// (/v1/subscription/*). É o que a marca CONTRATOU — quanto ela gastou é medição
// e mora em ./costs.js, que este módulo não importa e que não importa este.
//
// O que mantém as duas rotas juntas é o Stripe: o plano é um Produto lá, a
// assinatura é uma Subscription lá, e as duas ações de dinheiro do app
// (`startCheckout` e `openBillingPortal`) são portas para lá — nós nunca vemos
// cartão. Um módulo só de `/v1/plans` seria um GET solto que a tela de plano
// leria junto com a assinatura em toda renderização.
//
// `startCheckout` e `openBillingPortal` NASCERAM na seção de cadastro público de
// api.ts (o checkout é o último degrau do onboarding), mas vieram para cá: são
// /v1/subscription/*, e quem os chama é a tela de plano, o gate de assinatura, a
// faixa de carência e a exclusão de marca — nenhuma delas é cadastro. Deixá-los
// em ./signup.js faria quatro telas de dentro do app importar do módulo da porta
// pública.
//
// Só gestor: o back devolve 403 para o resto em tudo aqui, EXCETO
// `getSubscriptionAccess`, que é a única rota de assinatura que o vendedor
// alcança (ver a nota nela).
import { api, toApiError } from "./client.js";

export interface Plan {
  slug: string;
  name: string;
  monthlyPrice: number;
  includedSeats: number;
  includedMinutes: number;
  includedStorageGb: number;
  extraSeatPrice: number;
  extraMinutePrice: number;
  extraStorageGbPrice: number;
  // "Mais escolhido" — vem da coluna `plan.recommended` no back, com índice
  // único garantindo no máximo um. Opcional no tipo porque um back anterior
  // ao campo simplesmente não manda: aí nenhum card é destacado, que é o
  // estado honesto, em vez de a tela quebrar durante a janela de deploy.
  recommended?: boolean;
  // O que o plano entrega, em texto. A fonte é o metadata do Produto no Stripe;
  // o back espelha e devolve aqui. Opcional pelo mesmo motivo de `recommended`:
  // back anterior ao campo não manda, e a tela apenas não lista nada.
  features?: string[];
}

export interface Subscription {
  plan: { name: string; slug: string } | null;
  status: string | null;
  /** Contato de cobrança do cliente. `null` = cai no padrão (quem criou a conta). */
  billingEmail: string | null;
  /**
   * Telefone do mesmo contato, canonicalizado pelo back (55 + DDD + número).
   * `null` = não informado — aqui não há padrão para cair, ao contrário do
   * email.
   */
  billingPhone: string | null;
  /** Para onde os avisos de cobrança vão HOJE, já resolvido pelo back. */
  billingRecipient: string | null;
  // false = ainda não há assinatura no Stripe (marca nova, cancelada ou de
  // antes dos planos): a tela mostra "contratar", não "gerenciar". Sem
  // assinatura o back barra a marca no gate — plano é obrigatório para usar a
  // plataforma —, então este `false` sempre significa "precisa contratar".
  hasStripeSubscription: boolean;
  current: {
    competencia: string;
    monthly: number;
    seats: { peak: number; included: number; extra: number; total: number };
    minutes: { used: number; included: number; extra: number; total: number };
    storage: {
      usedGbMonth: number;
      includedGbMonth: number;
      extraGbMonth: number;
      total: number;
    };
    total: number;
  };
}

export async function getPlans(): Promise<Plan[]> {
  const { data, error } = await api<Plan[]>("/v1/plans");
  if (error) throw toApiError("GET /v1/plans", error);
  return data;
}

export async function getSubscription(): Promise<Subscription> {
  const { data, error } = await api<Subscription>("/v1/subscription");
  if (error) throw toApiError("GET /v1/subscription", error);
  return data;
}

/**
 * Quem é o contato de cobrança do cliente: o email que recebe recibo, pagamento
 * recusado e lembrete de assinatura pendente, e o telefone por onde falar com
 * essa pessoa. `null` no email limpa e volta ao padrão; `null` no telefone só
 * apaga.
 *
 * O telefone vai como a pessoa digitou (com máscara ou sem) — quem valida e
 * canonicaliza é o back, que devolve a forma gravada.
 */
export async function setBillingContact(contato: {
  billingEmail: string | null;
  billingPhone: string | null;
}): Promise<{
  billingEmail: string | null;
  billingPhone: string | null;
  billingRecipient: string | null;
}> {
  const { data, error } = await api<{
    billingEmail: string | null;
    billingPhone: string | null;
    billingRecipient: string | null;
  }>("/v1/subscription/billing-contact", { method: "PATCH", body: contato });
  if (error) throw toApiError("PATCH /v1/subscription/billing-contact", error);
  return data;
}

export interface SubscriptionAccess {
  active: boolean;
  status: string | null;
  graceDaysLeft: number | null;
}

// Estado do gate para QUALQUER membro — a única rota de assinatura que o
// vendedor alcança. Pobre de propósito: sem plano, sem preço, sem consumo (ver
// features/plans/controller.access no back). O front só precisa saber se
// redireciona e quantos dias de carência restam.
export async function getSubscriptionAccess(): Promise<SubscriptionAccess> {
  const { data, error } = await api<SubscriptionAccess>("/v1/subscription/access");
  if (error) throw toApiError("GET /v1/subscription/access", error);
  return data;
}

// Checkout do Stripe: contratar ou trocar de plano. Devolve só a URL — quem
// navega é a tela (`window.location.href`), porque sair do app é decisão dela.
export async function startCheckout(planSlug: string): Promise<{ url: string }> {
  const { data, error } = await api<{ url: string }>("/v1/subscription/checkout", {
    method: "POST",
    body: { planSlug },
  });
  if (error) throw toApiError("POST /v1/subscription/checkout", error);
  return data;
}

// Customer Portal do Stripe: cartão, faturas e cancelamento. Mesma forma do
// checkout, e pelo mesmo motivo.
export async function openBillingPortal(): Promise<{ url: string }> {
  const { data, error } = await api<{ url: string }>("/v1/subscription/portal", {
    method: "POST",
  });
  if (error) throw toApiError("POST /v1/subscription/portal", error);
  return data;
}
